import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import RevenueMetricsCards from '../components/revenue/RevenueMetricsCards';
import RevenueChart from '../components/revenue/RevenueChart';
import TopAdUnitsTable from '../components/revenue/TopAdUnitsTable';
import TopPartnersTable from '../components/revenue/TopPartnersTable';
import RevenueFilterBar from '../components/revenue/RevenueFilterBar';
import DateRangeSelector from '../components/DateRangeSelector';
import usePageMetadata from '../utils/usePageMetadata';
import './Revenue.css';

const Revenue = () => {
  usePageMetadata({
    title: 'Revenue Dashboard | The Ad Shop',
    description: 'Track and analyze your ad revenue performance with detailed AdSense reporting and insights.'
  });

  const { token, user } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topAdUnits, setTopAdUnits] = useState([]);
  const [topPartners, setTopPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState(7); // days
  const [filters, setFilters] = useState({ adPartner: 'all', adUnit: 'all', adSize: 'all', geo: 'all', autoAdType: 'all' });
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [availableFilters, setAvailableFilters] = useState({ adPartners: [], adUnits: [], adSizes: [], geos: [], autoAdTypes: [] });
  const [noDataMessage, setNoDataMessage] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sitesData = response.data.sites;
      setSites(sitesData);

      // Auto-select first site with AdSense connected
      const siteWithAdSense = sitesData.find(s => s.adsenseAccountId);
      if (siteWithAdSense) {
        setSelectedSite(siteWithAdSense);
      } else if (sitesData.length > 0) {
        setSelectedSite(sitesData[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setError('Failed to load sites');
      setLoading(false);
    }
  }, [token]);

  const fetchRevenueData = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const params = new URLSearchParams({
        adPartner: filters.adPartner || 'all',
        adUnit: filters.adUnit || 'all',
        adSize: filters.adSize || 'all',
        autoAdType: filters.autoAdType || 'all',
        siteId: selectedSite.siteId
      });
      
      // Handle custom date range or numeric days
      if (typeof timeRange === 'object' && timeRange?.custom) {
        params.append('startDate', timeRange.startDate);
        params.append('endDate', timeRange.endDate);
      } else {
        params.append('days', timeRange);
      }
      
      if (filters.geo && filters.geo !== 'all') {
        params.append('geo', filters.geo);
      }
      
      console.log('Fetching revenue data for site:', selectedSite.siteId);
      const response = await axios.get(
        `/api/revenue-data/${selectedSite.siteId}/overview?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Revenue data response:', response.data);
      const summary = response.data.summary;
      const dailyData = response.data.dailyData || [];
      
      // Check if there's truly no data (summary is null or all zeros, and no daily data)
      const hasNoData = !summary || 
        (summary.revenue?.value === 0 && 
         summary.pageviews?.value === 0 && 
         summary.adImpressions?.value === 0 && 
         dailyData.length === 0);
      
      if (hasNoData) {
        setRevenueData(null);
        setNoDataMessage('No revenue data available. Click "Refresh Data" to run a scan and sync revenue data from AdSense.');
        setError(''); // Clear any previous errors
      } else {
        setRevenueData(summary);
        setNoDataMessage(response.data.noDataMessage || '');
        setError(''); // Clear any previous errors
      }
      
      setDailyData(dailyData);
      setTopAdUnits(response.data.topAdUnits || []);
      setTopPartners(response.data.topPartners || []);
      setAvailableFilters(response.data.availableFilters || { adPartners: [], adUnits: [], adSizes: [], geos: [], autoAdTypes: [] });
    } catch (error) {
      console.error('Error fetching revenue:', error);
      console.error('Error response:', error.response?.data);
      
      // Clear previous data
      setRevenueData(null);
      setDailyData([]);
      setTopAdUnits([]);
      setTopPartners([]);
      
      if (error.response?.status === 404) {
        // 404 means no data found - show scan message
        setNoDataMessage('No revenue data available. Click "Refresh Data" to run a scan and sync revenue data from AdSense.');
        setError('');
      } else if (error.response?.status === 401) {
        // Authentication error
        setError('Authentication failed. Please log in again.');
        setNoDataMessage('');
      } else if (error.response?.status === 500 || error.response?.status >= 500) {
        // Server error - might be a real error or might be no data
        // Check if the error message suggests no data
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.toLowerCase().includes('no data') || errorMessage.toLowerCase().includes('not found')) {
          setNoDataMessage('No revenue data available. Click "Refresh Data" to run a scan and sync revenue data from AdSense.');
          setError('');
        } else {
          setError(errorMessage || 'Failed to load revenue data');
          setNoDataMessage('');
        }
      } else {
        // Other errors - check if it's a no-data scenario
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.toLowerCase().includes('no data') || errorMessage.toLowerCase().includes('not found')) {
          setNoDataMessage('No revenue data available. Click "Refresh Data" to run a scan and sync revenue data from AdSense.');
          setError('');
        } else {
          setError(errorMessage || 'Failed to load revenue data');
          setNoDataMessage('');
        }
      }
    }
  }, [filters.adPartner, filters.adUnit, filters.adSize, filters.geo, filters.autoAdType, selectedSite, timeRange, token]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const checkSyncStatus = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const response = await axios.get(
        `/api/analytics-data/${selectedSite.siteId}/sync-status?siteId=${selectedSite.siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error checking sync status:', error);
      setSyncStatus({ canSync: true, reason: 'Status check failed, allowing sync' });
    }
  }, [selectedSite, token]);

  const handleSync = async () => {
    if (!selectedSite || syncing) return;

    // Check sync status before attempting sync
    if (syncStatus && !syncStatus.canSync) {
      const minutesText = syncStatus.minutesRemaining 
        ? (syncStatus.minutesRemaining >= 60 
          ? `${Math.floor(syncStatus.minutesRemaining / 60)} hour${Math.floor(syncStatus.minutesRemaining / 60) !== 1 ? 's' : ''} ${syncStatus.minutesRemaining % 60} minute${syncStatus.minutesRemaining % 60 !== 1 ? 's' : ''}`
          : `${syncStatus.minutesRemaining} minute${syncStatus.minutesRemaining !== 1 ? 's' : ''}`)
        : `${syncStatus.minutesRemaining} minutes`;
      alert(`Please wait ${minutesText} before syncing again.`);
      return;
    }

    // Show notice only when fetching more than 90 days of data
    // Syncs fetch data based on plan retention days, so check if retention > 90
    const plan = syncStatus?.plan;
    const retentionDays = plan?.dataRetentionDays || 90;
    if (retentionDays > 90) {
      const proceed = window.confirm(
        `Syncing ${retentionDays} days of data may take longer than usual. Please be patient while we fetch your historical data.\n\nDo you want to continue?`
      );
      if (!proceed) {
        return;
      }
    }

    setSyncing(true);
    setError('');
    setSyncError(null);
    setSyncProgress(0);

    try {
      // Sync endpoint uses plan retention days, not the days parameter
      await axios.post(
        `/api/analytics-data/${selectedSite.siteId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data after sync
      await fetchRevenueData();
      await checkSyncStatus();
      
    } catch (error) {
      console.error('Sync error:', error);
      let errorMessage = 'Failed to sync revenue data. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setSyncError(errorMessage);
      setError(errorMessage);
    } finally {
      setSyncing(false);
      setSyncProgress(0);
    }
  };

  const handleClearData = async () => {
    if (!selectedSite || clearing) return;

    if (!window.confirm('Are you sure you want to clear all revenue data for this site? This action cannot be undone.')) {
      return;
    }

    try {
      setClearing(true);
      setError('');
      
      await axios.get(`/api/revenue-data/${selectedSite.siteId}/overview?clear=true&siteId=${selectedSite.siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear the current data display
      setRevenueData(null);
      setDailyData([]);
      setTopAdUnits([]);
      setTopPartners([]);
      setNoDataMessage('No revenue data available. Click "Refresh Data" to run a scan and sync revenue data from AdSense.');
      
      alert('✅ Revenue data cleared successfully! Click "Refresh Data" to get fresh data from AdSense.');
      
    } catch (error) {
      console.error('Error clearing data:', error);
      setError('Failed to clear revenue data. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  // Animate progress bar while syncing
  useEffect(() => {
    if (!syncing) {
      setSyncProgress(0);
      setSyncError(null);
      return;
    }

    const progressInterval = setInterval(() => {
      setSyncProgress(prev => {
        const next = prev + 2;
        return next >= 100 ? 0 : next;
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [syncing]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    if (selectedSite) {
      fetchRevenueData();
      checkSyncStatus();
    }
  }, [fetchRevenueData, checkSyncStatus, selectedSite]);

  if (loading) {
    return (
      <div className="revenue-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading revenue data...</p>
        </div>
      </div>
    );
  }

  const hasAdSenseConnected = selectedSite?.adsenseAccountId;
  const hasData = revenueData;
  const lastRefreshedLabel = syncStatus?.lastSync
    ? new Date(syncStatus.lastSync).toLocaleString()
    : null;
  const lastSyncText = syncing
    ? 'Refreshing now...'
    : lastRefreshedLabel
    ? `Last refreshed: ${lastRefreshedLabel}`
    : 'Last refreshed: Not synced yet';

  return (
    <div className="revenue-page">
      <div className="revenue-header">
        <div className="header-top">
          <div className="header-left">
            <div className="header-title-section">
              <h1>Revenue Dashboard</h1>
            </div>
            {selectedSite && (
              <div className="site-selector">
                <select
                  className="site-dropdown"
                  value={selectedSite?.siteId || ''}
                  onChange={(e) => {
                    const site = sites.find(s => s.siteId === e.target.value);
                    setSelectedSite(site);
                  }}
                >
                  {sites.map(site => (
                    <option key={site.siteId} value={site.siteId}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <DateRangeSelector
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
              maxDays={365}
            />
            {hasData && (
              <RevenueFilterBar
                onFiltersChange={setFilters}
                currentFilters={filters}
                availableFilters={availableFilters}
              />
            )}
          </div>
          
          <div className="header-right">
            {(hasAdSenseConnected || selectedSite) && (
              <div className="sync-controls">
                <div className="sync-buttons">
                  <div className="sync-primary">
                    <button 
                      className="btn-sync"
                      onClick={handleSync}
                      disabled={syncing || (syncStatus && !syncStatus.canSync)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0115.5-8.5l4 4M22 12.5a10 10 0 01-15.5 8.5l-4-4" />
                      </svg>
                      {syncing ? 'Syncing...' : 'Refresh Data'}
                    </button>
                    {syncing && (
                      <div className="sync-progress-bar">
                        <div 
                          className="sync-progress-fill" 
                          style={{ width: `${syncProgress}%` }}
                        ></div>
                      </div>
                    )}
                    {syncError && (
                      <div className="sync-error-message">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {syncError}
                      </div>
                    )}
                    <span className="last-updated" aria-live="polite">
                      {lastSyncText}
                    </span>
                  </div>
                  <button 
                    className="btn-clear-data"
                    onClick={handleClearData}
                    disabled={clearing}
                    title="Clear all revenue data for this site"
                    type="button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" />
                    </svg>
                    {clearing ? 'Clearing...' : 'Clear Data'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {syncStatus && !syncStatus.canSync && (
          <div className="rate-limit-warning">
            ⏱ Next refresh available in {syncStatus.minutesRemaining || 0} minutes
            {syncStatus.nextAvailableAt
              ? ` (${new Date(syncStatus.nextAvailableAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
              : ''}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {noDataMessage && !revenueData && (
        <div className="no-data-message">
          {noDataMessage}
        </div>
      )}

      {revenueData && (
        <>
          <RevenueMetricsCards
            data={revenueData}
            timeRange={timeRange}
            selectedMetric={selectedMetric}
            onMetricSelect={setSelectedMetric}
            selectedSite={selectedSite}
            user={user}
          />
          <RevenueChart
            data={dailyData}
            selectedMetric={selectedMetric}
            filters={filters}
            timeRange={timeRange}
          />
          <TopAdUnitsTable
            adUnits={topAdUnits}
            siteId={selectedSite?.siteId}
          />
          <TopPartnersTable
            partners={topPartners}
          />
        </>
      )}
    </div>
  );
};

export default Revenue;

